import {
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';

import { drivers as seedDrivers } from '../data/drivers';

import {
  defaultDriverFilters,
  filterDrivers,
  type DriverFilters,
} from '../data/driverFilters';

import type {
  DriverAvailability,
  DriverRecord,
} from '../types';

import {
  apiGetDrivers,
  apiDeleteDriver,
  apiUpdateDriverAvailability,
  apiCreateDriver,
  apiUpdateDriver,
  type CreateDriverPayload,
} from '../api/drivers/driversApi';

import {
  USE_DASHBOARD_REAL_BACKEND,
} from '../api/config/config';


/**
 * Owns the roster and filter state for the Drivers page.
 * Integrates real backend API calls with mock fallback support.
 */
export const useDrivers = () => {

  // ---------------------------------------
  // Driver records
  // ---------------------------------------

  const [records, setRecords] =
    useState<DriverRecord[]>(
      USE_DASHBOARD_REAL_BACKEND
        ? []
        : seedDrivers
    );


  // ---------------------------------------
  // Filters
  // ---------------------------------------

  const [filters, setFilters] =
    useState<DriverFilters>(
      defaultDriverFilters
    );


  // ---------------------------------------
  // Loading / Error state
  // ---------------------------------------

  const [isLoading, setIsLoading] =
    useState(true);

  const [error, setError] =
    useState<string | null>(null);


  // ---------------------------------------
  // Load all drivers
  // ---------------------------------------

  const loadDrivers = useCallback(
    async () => {

      setIsLoading(true);
      setError(null);

      try {

        const res =
          await apiGetDrivers();

        if (
          res.success &&
          Array.isArray(res.data)
        ) {

          setRecords(res.data);

          if (res.warning) {
            setError(res.warning);
          }

        } else if (
          Array.isArray(res.data)
        ) {

          setRecords(res.data);

          if (res.warning) {
            setError(res.warning);
          }

        } else {

          setError(
            res.message ||
              'Failed to load drivers.'
          );

          // Only use seed data when backend
          // is not being used.
          if (!USE_DASHBOARD_REAL_BACKEND) {
            setRecords(seedDrivers);
          }
        }

      } catch (err: any) {

        console.error(
          'Load drivers error:',
          err
        );

        setError(
          err?.message ||
            'Connection failed.'
        );

        if (!USE_DASHBOARD_REAL_BACKEND) {
          setRecords(seedDrivers);
        }

      } finally {

        setIsLoading(false);

      }

    },
    []
  );


  // ---------------------------------------
  // Load drivers when hook mounts
  // ---------------------------------------

  useEffect(() => {

    loadDrivers();

  }, [loadDrivers]);


  // ---------------------------------------
  // Apply filters
  // ---------------------------------------

  const visible = useMemo(
    () =>
      filterDrivers(
        records,
        filters
      ),
    [
      records,
      filters,
    ]
  );


  // ---------------------------------------
  // Update driver availability
  // ---------------------------------------

  const setAvailability =
    useCallback(
      async (
        id: string,
        availability: DriverAvailability
      ) => {

        // Store previous records in case
        // backend update fails.
        const previousRecords =
          records;

        // Optimistic update
        setRecords((current) =>
          current.map(
            (driver) =>
              driver.id === id
                ? {
                    ...driver,
                    availability,
                  }
                : driver
          )
        );

        try {

          const res =
            await apiUpdateDriverAvailability(
              id,
              availability
            );

          if (!res.success) {

            // Roll back optimistic update
            setRecords(
              previousRecords
            );

            setError(
              res.message ||
                'Failed to update driver availability.'
            );

            return false;
          }

          return true;

        } catch (err) {

          console.error(
            'Failed to update driver availability:',
            err
          );

          // Roll back
          setRecords(
            previousRecords
          );

          return false;
        }
      },
      [records]
    );


  // ---------------------------------------
  // Delete driver
  // ---------------------------------------

  const removeDriver =
    useCallback(
      async (id: string) => {

        // Keep the driver so we can restore it
        // if the backend deletion fails.
        const driverToDelete =
          records.find(
            (driver) =>
              driver.id === id
          );

        // Optimistic removal
        setRecords((current) =>
          current.filter(
            (driver) =>
              driver.id !== id
          )
        );

        try {

          const res =
            await apiDeleteDriver(id);

          if (!res.success) {

            // Restore driver
            if (driverToDelete) {

              setRecords((current) => {

                const alreadyExists =
                  current.some(
                    (driver) =>
                      driver.id === id
                  );

                if (alreadyExists) {
                  return current;
                }

                return [
                  driverToDelete,
                  ...current,
                ];
              });
            }

            setError(
              res.message ||
                'Failed to delete driver.'
            );

            return res;
          }

          return res;

        } catch (err: any) {

          console.error(
            'Failed to delete driver:',
            err
          );

          // Restore driver
          if (driverToDelete) {

            setRecords((current) => {

              const alreadyExists =
                current.some(
                  (driver) =>
                    driver.id === id
                );

              if (alreadyExists) {
                return current;
              }

              return [
                driverToDelete,
                ...current,
              ];
            });
          }

          return {
            success: false,
            message:
              err?.message ||
              'Failed to delete driver.',
          };
        }
      },
      [records]
    );


  // ---------------------------------------
  // Add new driver
  // ---------------------------------------

  const addDriver =
    useCallback(
      async (
        payload: CreateDriverPayload
      ) => {

        try {

          console.log(
            '========== ADD DRIVER =========='
          );

          console.log(
            'Payload received by useDrivers:',
            payload
          );

          const res =
            await apiCreateDriver(
              payload
            );

          console.log(
            'Create driver API response:',
            res
          );

          console.log(
            '================================'
          );


          if (
            res.success &&
            res.data
          ) {

            setRecords(
              (current) => [
                res.data!,
                ...current,
              ]
            );

            setError(null);
          }

          return res;

        } catch (err: any) {

          console.error(
            'Add driver error:',
            err
          );

          const errorResponse = {
            success: false,
            message:
              err?.message ||
              'Failed to create driver.',
          };

          setError(
            errorResponse.message
          );

          return errorResponse;
        }
      },
      []
    );


  // ---------------------------------------
  // Update existing driver
  // ---------------------------------------

  const updateDriver =
    useCallback(
      async (
        id: string,
        payload: Partial<CreateDriverPayload>
      ) => {

        try {

          console.log(
            '========== UPDATE DRIVER =========='
          );

          console.log(
            'Driver ID:',
            id
          );

          console.log(
            'Payload:',
            payload
          );

          const res =
            await apiUpdateDriver(
              id,
              payload
            );

          console.log(
            'Update driver API response:',
            res
          );

          console.log(
            '==================================='
          );


          if (
            res.success &&
            res.data
          ) {

            setRecords(
              (current) =>
                current.map(
                  (driver) =>
                    String(driver.id) ===
                    String(id)
                      ? res.data!
                      : driver
                )
            );

            setError(null);
          }

          return res;

        } catch (err: any) {

          console.error(
            'Update driver error:',
            err
          );

          const errorResponse = {
            success: false,
            message:
              err?.message ||
              'Failed to update driver.',
          };

          setError(
            errorResponse.message
          );

          return errorResponse;
        }
      },
      []
    );


  // ---------------------------------------
  // Reset filters
  // ---------------------------------------

  const resetFilters =
    useCallback(
      () => {

        setFilters(
          defaultDriverFilters
        );

      },
      []
    );


  // ---------------------------------------
  // Return hook API
  // ---------------------------------------

  return {

    // Data
    records,
    visible,

    // Filters
    filters,
    setFilters,
    resetFilters,

    // Driver actions
    setAvailability,
    removeDriver,
    addDriver,
    updateDriver,

    // Refresh
    refreshDrivers:
      loadDrivers,

    // State
    isLoading,
    error,
  };
};